import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormInput } from "../../../components/atoms/Input/Input";
import { FormLabel } from "../../../components/atoms/label";
import { ConfirmationModal } from "../../../components/organisms/Modal/confirmationModal";
import { useNotification } from "../../../hooks/useNotification";
import {
  useBulkSubmitPricelistsMutation,
  useBulkUnpublishPricelistsMutation,
  useDeletePricelistMutation,
} from "../../../store/apiSlice/pricingApiSlice";
import { componentWithCondition } from "../../newLayout/page/components/hoc/componentWithConditionCheck";
import { ButtonSpinner } from "../../../components/atoms/Button/Button.styles";
import { ListActionBurgerMenu } from "../../newLayout/page/components/OptionsForView/ListActionBurgerMenu";
import {
  apiErrorMessage,
  bulkStatusToast,
  deleteToast,
  pricelistDisplayName,
} from "../utils/toastMessages";

/** Resolve list-row status id (object or plain string). */
const statusIdOf = item => item?.status?.id || item?.status || null;

/**
 * List toolbar actions for Price Lists: create, update, bulk publish /
 * unpublish, and delete.
 *
 * @param {Object} props
 * @param {Array<number|string>} props.selectedOrderId Selected row PKs
 * @param {Function} props.setSelectedOrder Clears / updates selection
 * @param {Array<Object>} props.selectedOrderFromListview Full selected rows
 * @return {JSX.Element}
 */
const PriceListAction = ({ selectedOrderId, setSelectedOrder, selectedOrderFromListview }) => {
  const navigate = useNavigate();
  const notify = useNotification();
  const [deletePricelist, { isLoading: isDeleteLoading } = {}] = useDeletePricelistMutation();
  const [bulkSubmit, { isLoading: isPublishing } = {}] = useBulkSubmitPricelistsMutation();
  const [bulkUnpublish, { isLoading: isUnpublishing } = {}] = useBulkUnpublishPricelistsMutation();

  const [isDeleting, setIsDeleting] = useState(false);
  const [isPublishingConfirm, setIsPublishingConfirm] = useState(false);
  const [isUnpublishingConfirm, setIsUnpublishingConfirm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [pricelistName, setPricelistName] = useState("");

  const selectedRows = useMemo(() => selectedOrderFromListview || [], [selectedOrderFromListview]);

  const draftRows = useMemo(
    () => selectedRows.filter(row => statusIdOf(row) === "draft"),
    [selectedRows],
  );
  const publishedRows = useMemo(
    () => selectedRows.filter(row => statusIdOf(row) === "published"),
    [selectedRows],
  );

  const canPublish = draftRows.length > 0;
  const canUnpublish = publishedRows.length > 0;
  const busy = isPublishing || isUnpublishing;

  const handleUpdate = () => {
    if (selectedOrderId.length !== 1) {
      notify({
        type: "warning",
        message: "Select one price list to update.",
      });
      return;
    }
    const row =
      selectedRows.find(r => String(r.id) === String(selectedOrderId[0])) || selectedRows[0];
    if (statusIdOf(row) === "published") {
      // Open view-only editor (Unpublish available); content edits are locked.
      notify({
        type: "warning",
        message:
          "This price list is published. You can view it and Unpublish — unpublish before editing.",
      });
    }
    navigate(`/sales/pricelist/update/${selectedOrderId[0]}`);
  };

  const handleDelete = async () => {
    let ok = 0;
    const failures = [];
    for (const row of selectedRows) {
      try {
        await deletePricelist(row.id).unwrap();
        ok += 1;
      } catch (error) {
        failures.push({
          id: row.id,
          detail: apiErrorMessage(error?.data, "Delete failed"),
        });
      }
    }

    if (ok > 0 && failures.length === 0) {
      notify(deleteToast(ok));
    } else if (ok > 0 && failures.length > 0) {
      notify({
        type: "warning",
        message: `${ok} deleted, ${failures.length} could not be deleted. ${failures
          .map(f => {
            const name = (selectedRows.find(r => r.id === f.id)?.name || "").trim();
            const detail = apiErrorMessage(f.detail, "Unknown error");
            return name ? `"${name}". ${detail}` : detail;
          })
          .join(" ")}`,
      });
    } else if (failures.length) {
      notify(deleteToast(selectedRows.length, { data: { detail: failures[0].detail } }));
    }

    setSelectedOrder([]);
    setIsDeleting(false);
  };

  /**
   * Publish selected drafts via POST /pricelist/bulk_submit/.
   */
  const handlePublish = async () => {
    try {
      const result = await bulkSubmit(draftRows.map(row => row.id)).unwrap();
      const toast = bulkStatusToast("publish", result, draftRows);
      if (toast) {
        notify(toast);
      }
    } catch (error) {
      notify({
        type: "error",
        message: apiErrorMessage(
          error?.data,
          "Could not publish the selected price lists. Please try again.",
        ),
      });
    }
    setSelectedOrder([]);
    setIsPublishingConfirm(false);
  };

  /**
   * Unpublish selected published lists via POST /pricelist/bulk_unpublish/.
   */
  const handleUnpublish = async () => {
    try {
      const result = await bulkUnpublish(publishedRows.map(row => row.id)).unwrap();
      const toast = bulkStatusToast("unpublish", result, publishedRows);
      if (toast) {
        notify(toast);
      }
    } catch (error) {
      notify({
        type: "error",
        message: apiErrorMessage(
          error?.data,
          "Could not unpublish the selected price lists. Please try again.",
        ),
      });
    }
    setSelectedOrder([]);
    setIsUnpublishingConfirm(false);
  };

  const handleCreateRedirect = () => {
    if (!pricelistName.trim()) {
      notify({ type: "warning", message: "Enter a name for the new price list." });
      return;
    }
    navigate("/sales/pricelist/create", { state: { name: pricelistName } });
    setIsCreating(false);
    setPricelistName("");
  };

  const publishConfirmCopy =
    draftRows.length === 1
      ? `Publish ${pricelistDisplayName(draftRows[0])}? It will become available for orders after publish checks pass.`
      : `Publish ${draftRows.length} draft price lists? Each list is checked for items and conflicts before publishing.`;

  const unpublishConfirmCopy =
    publishedRows.length === 1
      ? `Unpublish ${pricelistDisplayName(publishedRows[0])}? It will move to draft and stop applying to new orders.`
      : `Unpublish ${publishedRows.length} price lists? They will move to draft and stop applying to new orders.`;

  const deleteConfirmCopy =
    selectedRows.length === 1
      ? `Delete ${pricelistDisplayName(selectedRows[0])}? This cannot be undone.`
      : `Delete ${selectedRows.length} price lists? Published lists must be unpublished first. This cannot be undone.`;

  const burgerItems = useMemo(
    () => [
      {
        key: "unpublish",
        label: "Unpublish",
        onClick: () => setIsUnpublishingConfirm(true),
        disabled: !canUnpublish || busy,
        title: canUnpublish
          ? `Unpublish ${publishedRows.length} published list(s)`
          : "Select one or more Published price lists",
        isLoading: isUnpublishing,
      },
      {
        key: "delete",
        label: "Delete",
        onClick: () => setIsDeleting(true),
        disabled: selectedOrderId.length === 0 || busy || isDeleteLoading,
        title: selectedOrderId.length > 0 ? "" : "Select at least one price list to delete.",
        isLoading: isDeleteLoading,
        isDanger: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canUnpublish, busy, publishedRows.length, isUnpublishing, selectedOrderId.length, isDeleteLoading],
  );

  return (
    <>
      <li className={`tableButton activeState`}>
        <button onClick={() => setIsCreating(true)}>Create</button>
      </li>
      <li
        className={`tableButton ${selectedOrderId.length === 1 ? "activeState" : "disableState"}`}
      >
        <button
          onClick={handleUpdate}
          disabled={selectedOrderId.length !== 1}
          title={selectedOrderId.length === 1 ? "" : "Select a single price list to update."}
        >
          Update
        </button>
      </li>
      <li className={`tableButton ${canPublish && !busy ? "activeState" : "disableState"}`}>
        <button
          onClick={() => setIsPublishingConfirm(true)}
          disabled={!canPublish || busy}
          title={
            canPublish
              ? `Publish ${draftRows.length} draft list(s)`
              : "Select one or more Draft price lists"
          }
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isPublishing && <ButtonSpinner aria-hidden="true" />}
          {isPublishing ? "Publishing…" : "Publish"}
        </button>
      </li>

      <ListActionBurgerMenu items={burgerItems} />

      {isCreating && (
        <ConfirmationModal
          titleOfModal="Create Pricelist"
          btnName="Create Pricelist"
          closeModal={() => setIsCreating(false)}
          handleModalWork={handleCreateRedirect}
          description={
            <>
              <FormLabel>Pricelist Name</FormLabel>
              <FormInput
                autoFocus
                value={pricelistName}
                onChange={e => setPricelistName(e.target.value)}
                placeholder="Enter Pricelist Name"
              />
            </>
          }
        />
      )}

      {isPublishingConfirm && (
        <ConfirmationModal
          titleOfModal="Publish Price List"
          btnName="Publish"
          closeModal={() => setIsPublishingConfirm(false)}
          handleModalWork={handlePublish}
          isLoading={isPublishing}
          description={publishConfirmCopy}
        />
      )}

      {isUnpublishingConfirm && (
        <ConfirmationModal
          titleOfModal="Unpublish Price List"
          btnName="Unpublish"
          closeModal={() => setIsUnpublishingConfirm(false)}
          handleModalWork={handleUnpublish}
          isLoading={isUnpublishing}
          description={unpublishConfirmCopy}
        />
      )}

      {isDeleting && (
        <ConfirmationModal
          titleOfModal="Delete Price List"
          btnName="Delete"
          closeModal={() => setIsDeleting(false)}
          handleModalWork={handleDelete}
          description={deleteConfirmCopy}
        />
      )}
    </>
  );
};

export const PriceListActionWithCheck = componentWithCondition(PriceListAction);
